/**
 * FEATURE-011: Similar Scenarios Warning Component
 *
 * Displays when the parser detects scenario names that are likely the same
 * but have minor differences (whitespace, case, etc.). Allows users to
 * merge them into a single canonical name.
 *
 * @param {Object} props
 * @param {Object} props.similarScenarios - Detection results from backend
 * @param {boolean} props.similarScenarios.has_similar - Whether similar scenarios exist
 * @param {Array} props.similarScenarios.groups - Groups of similar scenarios
 * @param {Function} props.onMergeChange - Callback when user changes merge selections
 * @param {Array} props.mergeSelections - Current merge selections state
 */
function SimilarScenariosWarning({ similarScenarios, onMergeChange, mergeSelections }) {
  // Don't render if no similar scenarios
  if (!similarScenarios?.has_similar || !similarScenarios?.groups?.length) {
    return null
  }

  const groups = similarScenarios.groups

  // Handle checkbox change for a group
  const handleMergeToggle = (groupIndex) => {
    const updated = [...mergeSelections]
    updated[groupIndex] = {
      ...updated[groupIndex],
      merge: !updated[groupIndex].merge,
    }
    onMergeChange(updated)
  }

  // Handle canonical name selection change
  const handleCanonicalChange = (groupIndex, newCanonical) => {
    const updated = [...mergeSelections]
    updated[groupIndex] = {
      ...updated[groupIndex],
      canonical_name: newCanonical,
    }
    onMergeChange(updated)
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-amber-500"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="text-sm text-amber-800">
          <p className="font-semibold text-base">Similar scenario names detected</p>
          <p className="mt-1 text-amber-700">
            The following scenarios have similar names and may be duplicates. This often happens when
            Synchro files have inconsistent spacing or capitalization.
          </p>
        </div>
      </div>

      {/* Scenario Groups */}
      <div className="space-y-4 ml-8">
        {groups.map((group, groupIndex) => {
          const selection = mergeSelections[groupIndex] || {
            merge: true,
            canonical_name: group.canonical,
          }

          return (
            <div key={group.normalized} className="bg-white rounded-lg border border-amber-100 p-4">
              {/* Group Header */}
              <div className="text-sm font-medium text-gray-700 mb-3">
                {groups.length > 1 ? `Group ${groupIndex + 1}` : 'Found these variations:'}
              </div>

              {/* Variant List */}
              <ul className="space-y-2 mb-4">
                {group.variants.map((variant) => (
                  <li key={variant.name} className="text-sm">
                    <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                      &quot;{variant.name}&quot;
                    </span>
                    {variant.files && variant.files.length > 0 && (
                      <span className="text-gray-500 ml-2">
                        (in: {variant.files.join(', ')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Merge Checkbox */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id={`merge-${groupIndex}`}
                  checked={selection.merge}
                  onChange={() => handleMergeToggle(groupIndex)}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                />
                <label
                  htmlFor={`merge-${groupIndex}`}
                  className="text-sm font-medium text-gray-700"
                >
                  Merge into single scenario
                </label>
              </div>

              {/* Canonical Name Selector (only shown when merge is checked) */}
              {selection.merge && (
                <div className="ml-6">
                  <label
                    htmlFor={`canonical-${groupIndex}`}
                    className="block text-sm text-gray-600 mb-1"
                  >
                    Use name:
                  </label>
                  <select
                    id={`canonical-${groupIndex}`}
                    value={selection.canonical_name}
                    onChange={(e) => handleCanonicalChange(groupIndex, e.target.value)}
                    className="block w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                  >
                    {/* Normalized (canonical) option first */}
                    <option value={group.canonical}>{group.canonical} (normalized)</option>

                    {/* Original variants as options */}
                    {group.variants.map((variant) => {
                      // Don't duplicate if variant name equals canonical
                      if (variant.name === group.canonical) return null
                      return (
                        <option key={variant.name} value={variant.name}>
                          {variant.name} (as found)
                        </option>
                      )
                    })}
                  </select>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Help Section */}
      <div className="mt-4 ml-8 bg-amber-100/50 rounded-lg p-3">
        <p className="text-sm font-medium text-amber-800 mb-2">What should I do?</p>
        <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
          <li>
            <strong>If these are the SAME scenario:</strong> Check &quot;Merge&quot; and pick a name
          </li>
          <li>
            <strong>If these are DIFFERENT scenarios:</strong> Uncheck &quot;Merge&quot; to keep both
          </li>
          <li>
            <strong>To fix permanently:</strong> Update your Synchro files to use consistent naming,
            then re-export
          </li>
        </ul>
      </div>
    </div>
  )
}

export default SimilarScenariosWarning
